import React, { useState, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { LinkPlugin as LexicalLinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import ToolbarPlugin from './ToolbarPlugin';
import AIToolbarPlugin from './AIToolbarPlugin';
import CustomLinkPlugin from './LinkPlugin';
import ContextPanel from './ContextPanel';
import WriteSettingsPanel from './WriteSettingsPanel';
import PricingPopup from '../PricingPopup';
import TutorialOverlay from '../../UI/TutorialOverlay';
import api from '../../../components/api';

import './Editor.css'

// Editor theme
const theme = {
    // Thème léger par défaut
    paragraph: 'editor-paragraph',
    text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
    },
    heading: {
        h1: 'editor-heading-h1',
        h2: 'editor-heading-h2',
        h3: 'editor-heading-h3',
    },
    list: {
        ul: 'editor-list-ul',
        ol: 'editor-list-ol',
    },
    link: 'editor-link',
};

function onError(error) {
    console.error(error);
}

const EditorContentLoader = ({ initialContent }) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        // Fonction qui met à jour le contenu de l'éditeur avec le contenu JSON
        editor.update(() => {
            const editorState = editor.parseEditorState(initialContent);
            editor.setEditorState(editorState);
        });
    }, [editor, initialContent]);

    return null; // Pas de rendu, cette fonction se contente de charger le contenu
};

const Placeholder = () => {
    return (
        <div className="editor-placeholder">
            Start writing...
        </div>
    );
};

// Définition des étapes du tutoriel avec des sélecteurs CSS précis
const tutorialSteps = [
    {
        selector: 'button[aria-label="Toggle Write Settings Panel"]',
        title: 'Configure Your Writing',
        content: 'Click here to set up your writing preferences. You can specify the language, tone, and provide examples of your preferred writing style.',
        position: 'bottom'
    },
    {
        selector: 'button[aria-label="Toggle Context Panel"]',
        title: 'Add Guidelines and References',
        content: 'Use this button to manage guidelines and references that will help the AI understand your needs better and write more relevant content.',
        position: 'bottom'
    },
    {
        selector: '.toolbar',
        title: 'Format Your Text',
        content: 'Use these tools to format your text, add headings, lists, and links.',
        position: 'bottom'
    },
    {
        selector: '.ai-toolbar',
        title: 'AI Writing Assistance',
        content: 'Here you can access powerful AI tools to expand, shorten, rewrite, or generate content based on the context.',
        position: 'top'
    }
];

const Editor = ({ project, isNewProject, onOpenPricing }) => {
    const initialConfig = {
        namespace: 'BookEditor',
        theme,
        onError,
        // Configuration complète des types de nœuds supportés
        nodes: [
            HeadingNode,
            LinkNode,
            AutoLinkNode,
            ListNode,
            ListItemNode
        ],
    };

    const [isLoading, setIsLoading] = useState(false); 
    const [showContextPanel, setShowContextPanel] = useState(false);
    const [showWriteSettingsPanel, setShowWriteSettingsPanel] = useState(false);
    
    // État pour le tutoriel
    const [showTutorial, setShowTutorial] = useState(false);
    
    // États pour stocker les données des panneaux
    const [projectSettings, setProjectSettings] = useState({
        language: 'English',
        description: '',
        writing_style: '',
        tone: 'Informative',
        nsfw: false,
    });
    const [guidelineContexts, setGuidelineContexts] = useState([]);
    const [referenceContexts, setReferenceContexts] = useState([]);
    const [contextsLoaded, setContextsLoaded] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    // Vérifier si c'est la première utilisation de l'application
    useEffect(() => {
        const tutorialCompleted = localStorage.getItem('tutorialCompleted');
        if (!tutorialCompleted) {
            // Attendre que l'interface soit complètement chargée
            setTimeout(() => {
                setShowTutorial(true);
            }, 1500);
        }
    }, []);

    // Charger les paramètres du projet une seule fois
    useEffect(() => {
        fetchProjectSettings();
    }, [project.id]);

    // Charger les contextes une seule fois
    useEffect(() => {
        fetchGuidelineContexts();
        fetchReferenceContexts();
    }, [project.id]);

    // Récupérer les paramètres du projet depuis le serveur
    const fetchProjectSettings = async () => {
        try {
            const response = await api.get(`/projects/${project.id}`);
            const projectData = response.data;
            
            setProjectSettings({
                language: projectData.language || 'English',
                description: projectData.description || '',
                writing_style: projectData.writing_style || '',
                tone: projectData.tone || 'Informative',
                nsfw: !!projectData.nsfw,
            });
            setSettingsLoaded(true);
        } catch (err) {
            console.error('Error fetching project settings:', err);
        }
    };

    // Récupérer les guidelines
    const fetchGuidelineContexts = async () => {
        try {
            const response = await api.get(`/contexts?project_id=${project.id}&type=guideline`);
            setGuidelineContexts(response.data);
            setContextsLoaded(true);
        } catch (err) {
            console.error('Error fetching guidelines:', err);
        }
    };

    // Récupérer les références
    const fetchReferenceContexts = async () => {
        try {
            const response = await api.get(`/contexts?project_id=${project.id}&type=reference`);
            setReferenceContexts(response.data);
            setContextsLoaded(true);
        } catch (err) {
            console.error('Error fetching references:', err);
        }
    };

    // Fonction pour mettre à jour les paramètres après sauvegarde
    const handleSettingsUpdate = (updatedSettings) => {
        setProjectSettings(updatedSettings);
    };

    // Fonction pour mettre à jour les contextes après ajout/modification/suppression
    const handleContextsUpdate = (type, updatedContexts) => {
        if (type === 'guideline') {
            setGuidelineContexts(updatedContexts);
        } else if (type === 'reference') {
            setReferenceContexts(updatedContexts);
        }
    };

    // Fonction pour gérer l'affichage des panneaux
    const toggleContextPanel = () => {
        setShowContextPanel(!showContextPanel);
        // Fermer l'autre panneau s'il est ouvert
        if (!showContextPanel && showWriteSettingsPanel) {
            setShowWriteSettingsPanel(false);
        }
    };

    const toggleWriteSettingsPanel = () => {
        setShowWriteSettingsPanel(!showWriteSettingsPanel);
        // Fermer l'autre panneau s'il est ouvert
        if (!showWriteSettingsPanel && showContextPanel) {
            setShowContextPanel(false);
        }
    };

    // Fonction pour gérer la fin du tutoriel
    const handleTutorialComplete = () => {
        setShowTutorial(false);
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className={`editor-container ${isLoading ? 'loading' : ''}`}>
                <EditorContentLoader initialContent={project.content} />
                
                <ToolbarPlugin 
                    projectId={project.id} 
                    isLoading={isLoading}
                    project={project}
                    onToggleContextPanel={toggleContextPanel}
                    showContextPanel={showContextPanel}
                    onToggleWriteSettingsPanel={toggleWriteSettingsPanel}
                    showWriteSettingsPanel={showWriteSettingsPanel}
                />
                
                <div className="editor-layout">
                    <div className={`editor-inner ${showContextPanel || showWriteSettingsPanel ? 'with-panel' : ''}`}>
                        <RichTextPlugin
                            contentEditable={<ContentEditable className={`content-editable ${isLoading ? 'disabled' : ''}`} />}
                            placeholder={<Placeholder />}
                        />
                        
                        {/* Plugins de base */}
                        <HistoryPlugin />
                        <AutoFocusPlugin />
                        
                        {/* Plugins pour les fonctionnalités spécifiques */}
                        <ListPlugin />
                        <LexicalLinkPlugin />
                        <CustomLinkPlugin />
                        
                        {/* Plugin AI */}
                        <AIToolbarPlugin 
                            onOpenPricing={onOpenPricing} 
                            projectId={project.id} 
                            onLoading={setIsLoading} 
                            isNewProject={isNewProject} 
                        />
                    </div>
                    
                    {showContextPanel && contextsLoaded && (
                        <ContextPanel 
                            projectId={project.id}
                            guidelineContexts={guidelineContexts}
                            referenceContexts={referenceContexts}
                            onContextsUpdate={handleContextsUpdate}
                            onClose={toggleContextPanel}
                        />
                    )}

                    {showWriteSettingsPanel && settingsLoaded && (
                        <WriteSettingsPanel 
                            projectId={project.id}
                            initialSettings={projectSettings}
                            onSettingsUpdate={handleSettingsUpdate}
                            onClose={toggleWriteSettingsPanel}
                        />
                    )}
                </div>
                
                {/* Tutoriel - Ajout en dehors de l'editor-layout pour éviter les problèmes de z-index */}
                <TutorialOverlay 
                    steps={tutorialSteps}
                    onComplete={handleTutorialComplete}
                    isVisible={showTutorial}
                />
            </div>
        </LexicalComposer>
    );
};

export default Editor;
